import { PLATFORM_SPECS } from './platforms';

export type CampaignStatus = 'live' | 'paused' | 'ended';

export interface PlatformPerformance {
  platform: 'Meta' | 'LinkedIn';
  format: string;
  spendUsd: number;
  leads: number;
  qualifiedLeads: number;
  benchmarkCplUsd: number;
}

export interface CampaignPerformance {
  id: string;
  name: string;
  team: string;
  status: CampaignStatus;
  dayOfRun: number;
  totalDays: number;
  plannedSpendUsd: number;
  cplTargetUsd: number;
  qualifiedRateFloor: number;
  minLeadsBeforeAction: number;
  budgetStepPercent: number;
  selfHealing: boolean;
  chart: { platform: PlatformPerformance['platform']; dailyCplUsd: number[] };
  platforms: PlatformPerformance[];
  proposal: { title: string; detail: string } | null;
  actionLog: { id: string; text: string; when: string }[];
}

// Benchmarks are the PRD's May–Jul matured CPLs; everything else is sample data.
const META_BENCHMARK_CPL = PLATFORM_SPECS.meta.benchmarkCplUsd ?? 0;
const LINKEDIN_BENCHMARK_CPL = PLATFORM_SPECS.linkedin.benchmarkCplUsd ?? 0;

export const FIXTURE_CAMPAIGNS: CampaignPerformance[] = [
  {
    id: 'c_revops_webinar',
    name: 'Webinar promo — RevOps series',
    team: 'Studio',
    status: 'live',
    dayOfRun: 8,
    totalDays: 14,
    plannedSpendUsd: 9800,
    cplTargetUsd: 40,
    qualifiedRateFloor: 0.35,
    minLeadsBeforeAction: 50,
    budgetStepPercent: 20,
    selfHealing: true,
    chart: { platform: 'Meta', dailyCplUsd: [38, 41, 36, 44, 39, 47, 53, 58] },
    platforms: [
      { platform: 'Meta', format: 'sponsored', spendUsd: 4120, leads: 79, qualifiedLeads: 25, benchmarkCplUsd: META_BENCHMARK_CPL },
      { platform: 'LinkedIn', format: 'boosted', spendUsd: 1860, leads: 11, qualifiedLeads: 8, benchmarkCplUsd: LINKEDIN_BENCHMARK_CPL },
    ],
    proposal: {
      title: 'Proposed: step Meta budget down',
      detail: 'Meta CPL is $52 against a $40 target, and qualified rate is 32%. Move Meta from $500 to $400/day.',
    },
    actionLog: [
      { id: 'a3', text: 'Alert sent to #growth-ads: CPL above target', when: 'Today' },
      { id: 'a2', text: 'LinkedIn audience ready, ad set switched on', when: 'Day 3' },
      { id: 'a1', text: 'Published by Growth · Salesforce campaign created', when: 'Day 1' },
    ],
  },
  {
    id: 'c_london_dinner',
    name: 'London field dinner invites',
    team: 'Field marketing',
    status: 'paused',
    dayOfRun: 6,
    totalDays: 10,
    plannedSpendUsd: 1500,
    cplTargetUsd: 60,
    qualifiedRateFloor: 0.4,
    minLeadsBeforeAction: 8,
    budgetStepPercent: 20,
    selfHealing: true,
    chart: { platform: 'Meta', dailyCplUsd: [72, 84, 95, 101, 96, 112] },
    platforms: [
      { platform: 'Meta', format: 'sponsored', spendUsd: 840, leads: 9, qualifiedLeads: 3, benchmarkCplUsd: META_BENCHMARK_CPL },
    ],
    proposal: null,
    actionLog: [
      { id: 'a3', text: 'Paused by self-healing: CPL 56% over target, qualified rate 33%', when: 'Yesterday' },
      { id: 'a2', text: 'Budget stepped down $150 → $120/day', when: 'Day 4' },
      { id: 'a1', text: 'Published by Growth · UK geo override approved', when: 'Day 1' },
    ],
  },
  {
    id: 'c_launch_recap',
    name: 'Launch recap video',
    team: 'Social',
    status: 'live',
    dayOfRun: 11,
    totalDays: 14,
    plannedSpendUsd: 9800,
    cplTargetUsd: 45,
    qualifiedRateFloor: 0.35,
    minLeadsBeforeAction: 50,
    budgetStepPercent: 20,
    selfHealing: true,
    chart: { platform: 'Meta', dailyCplUsd: [34, 31, 33, 30, 32, 29, 31, 33, 32, 30, 32] },
    platforms: [
      { platform: 'Meta', format: 'sponsored', spendUsd: 5280, leads: 165, qualifiedLeads: 66, benchmarkCplUsd: META_BENCHMARK_CPL },
      { platform: 'LinkedIn', format: 'boosted', spendUsd: 2200, leads: 12, qualifiedLeads: 9, benchmarkCplUsd: LINKEDIN_BENCHMARK_CPL },
    ],
    proposal: null,
    actionLog: [
      { id: 'a2', text: 'Weekly check: on target, no action', when: 'Day 7' },
      { id: 'a1', text: 'Published by Growth · Salesforce campaign created', when: 'Day 1' },
    ],
  },
  {
    id: 'c_case_study',
    name: 'Case study retargeting',
    team: 'PMM',
    status: 'live',
    dayOfRun: 5,
    totalDays: 21,
    plannedSpendUsd: 4200,
    cplTargetUsd: 250,
    qualifiedRateFloor: 0.5,
    minLeadsBeforeAction: 10,
    budgetStepPercent: 20,
    selfHealing: true,
    chart: { platform: 'LinkedIn', dailyCplUsd: [310, 240, 190, 220, 175] },
    platforms: [
      { platform: 'LinkedIn', format: 'sponsored', spendUsd: 1000, leads: 5, qualifiedLeads: 4, benchmarkCplUsd: LINKEDIN_BENCHMARK_CPL },
    ],
    proposal: null,
    actionLog: [
      { id: 'a2', text: 'Holding: 5 of 10 leads needed before any budget call', when: 'Today' },
      { id: 'a1', text: 'Published by Growth · company list added to reach 10,000 minimum', when: 'Day 1' },
    ],
  },
  {
    id: 'c_q3_launch',
    name: 'Q3 product launch boost',
    team: 'Social',
    status: 'ended',
    dayOfRun: 14,
    totalDays: 14,
    plannedSpendUsd: 2800,
    cplTargetUsd: 220,
    qualifiedRateFloor: 0.5,
    minLeadsBeforeAction: 10,
    budgetStepPercent: 20,
    selfHealing: false,
    chart: { platform: 'LinkedIn', dailyCplUsd: [240, 205, 190, 180, 172, 168, 175, 181, 170, 165, 172, 178, 169, 174] },
    platforms: [
      { platform: 'LinkedIn', format: 'boosted', spendUsd: 2800, leads: 16, qualifiedLeads: 11, benchmarkCplUsd: LINKEDIN_BENCHMARK_CPL },
    ],
    proposal: null,
    actionLog: [
      { id: 'a2', text: 'Ended on schedule · 16 leads synced to Salesforce', when: 'Sep 30' },
      { id: 'a1', text: 'Published by Growth', when: 'Sep 17' },
    ],
  },
  {
    id: 'c_gtme_guide',
    name: 'GTM engineering guide download',
    team: 'Content',
    status: 'ended',
    dayOfRun: 21,
    totalDays: 21,
    plannedSpendUsd: 10500,
    cplTargetUsd: 35,
    qualifiedRateFloor: 0.35,
    minLeadsBeforeAction: 50,
    budgetStepPercent: 20,
    selfHealing: true,
    chart: {
      platform: 'Meta',
      dailyCplUsd: [33, 31, 29, 30, 27, 28, 26, 29, 27, 28, 26, 27, 29, 28, 27, 26, 28, 29, 27, 28, 27],
    },
    platforms: [
      { platform: 'Meta', format: 'sponsored', spendUsd: 10500, leads: 372, qualifiedLeads: 141, benchmarkCplUsd: META_BENCHMARK_CPL },
    ],
    proposal: null,
    actionLog: [
      { id: 'a3', text: 'Ended on schedule · 372 leads synced to Salesforce', when: 'Sep 26' },
      { id: 'a2', text: 'Budget stepped up $450 → $500/day (under target 7 days)', when: 'Sep 12' },
      { id: 'a1', text: 'Published by Growth', when: 'Sep 5' },
    ],
  },
];

export interface CampaignTotals {
  spendUsd: number;
  leads: number;
  qualifiedLeads: number;
  cplUsd: number;
  qualifiedRate: number;
  cpqlUsd: number;
  overTarget: boolean;
  belowFloor: boolean;
  daysOverTarget: number;
}

export function campaignTotals(c: CampaignPerformance): CampaignTotals {
  const spendUsd = c.platforms.reduce((s, p) => s + p.spendUsd, 0);
  const leads = c.platforms.reduce((s, p) => s + p.leads, 0);
  const qualifiedLeads = c.platforms.reduce((s, p) => s + p.qualifiedLeads, 0);
  const cplUsd = spendUsd / leads;
  const qualifiedRate = qualifiedLeads / leads;
  const lastUnderTarget = [...c.chart.dailyCplUsd].reverse().findIndex(v => v <= c.cplTargetUsd);
  return {
    spendUsd,
    leads,
    qualifiedLeads,
    cplUsd,
    qualifiedRate,
    cpqlUsd: spendUsd / qualifiedLeads,
    overTarget: cplUsd > c.cplTargetUsd,
    belowFloor: qualifiedRate < c.qualifiedRateFloor,
    daysOverTarget: lastUnderTarget === -1 ? c.chart.dailyCplUsd.length : lastUnderTarget,
  };
}

export function findCampaign(id: string): CampaignPerformance | undefined {
  return FIXTURE_CAMPAIGNS.find(c => c.id === id);
}
